import {
  applyCurrencyTemplate,
  currencyMeta,
  formatMoney,
  groupThousands,
  type CurrencyFormatSettings,
} from "./format-money";

/** Glyph budget before compaction kicks in.
 *
 *  Sized for the storefront calendar's day cell (44 px wide on mobile), where
 *  a 7-glyph price like "¥12,000" still reads cleanly at 11 px with
 *  `tabular-nums` but an 8-glyph one does not. A soft target, not a guarantee:
 *  a currency whose SYMBOL is long (CHF, د.ك) can exceed it even compacted,
 *  and inventing a shorter symbol would be worse than overflowing by a glyph. */
export const DEFAULT_APPROX_MAX_LENGTH = 7;

/** Below this many major units, dropping the fractional part is too large a
 *  distortion to do silently (at 100, ".99" is under 1%). Above it, whole
 *  units always win — a calendar cell has no room for "1,250.00". */
const WHOLE_UNITS_FLOOR = 100;

/** Bidi isolate controls are zero-width. They must not count against the glyph
 *  budget, or an RTL currency measures two glyphs wider than it draws and
 *  compacts earlier than it needs to. */
const BIDI_CONTROLS = /[⁦-⁩‎‏]/g;

function visibleLength(text: string): number {
  return text.replace(BIDI_CONTROLS, "").length;
}

/** Largest-first so the first match is the right magnitude. Suffixes are
 *  deliberately NOT localised: the one surface that uses this is marked
 *  `translate="no"` (Google's DOM pass mangles calendar text), and
 *  `Intl.NumberFormat({notation:"compact"})` would discard the agency's own
 *  symbol, separators and display template — the three things `formatMoney`
 *  exists to honour. */
const UNITS = [
  { divisor: 1e9, suffix: "B" },
  { divisor: 1e6, suffix: "M" },
  { divisor: 1e3, suffix: "K" },
] as const;

function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/** Significant-digit budget: ~3 across the board, so a compacted price's
 *  relative error never exceeds ~0.5%. */
function decimalsFor(scaled: number): number {
  if (scaled < 10) return 2;
  if (scaled < 100) return 1;
  return 0;
}

/** Render `value` with `decimals` places, drop trailing zeros, and swap in the
 *  agency's decimal separator. Only strips zeros when a decimal point is
 *  actually present — otherwise "10" would lose its trailing digit. */
function renderScaled(
  value: number,
  decimals: number,
  decimalSeparator: string,
): string {
  const fixed = value.toFixed(decimals);
  const trimmed = fixed.includes(".")
    ? fixed.replace(/0+$/, "").replace(/\.$/, "")
    : fixed;
  return trimmed.replace(".", decimalSeparator);
}

interface CurrencyShape {
  code: string;
  symbol: string;
  decimals: number;
}

/** Render `major` with exactly `places` decimals, grouped and templated. */
function renderFixed(
  major: number,
  places: number,
  negative: boolean,
  settings: CurrencyFormatSettings,
  meta: CurrencyShape,
): string {
  const fixed = major.toFixed(places);
  const dot = fixed.indexOf(".");
  const intPart = dot === -1 ? fixed : fixed.slice(0, dot);
  const fracPart = dot === -1 ? "" : fixed.slice(dot + 1);
  const grouped = groupThousands(intPart, settings.thousandSeparator);
  const amount =
    fracPart === ""
      ? grouped
      : `${grouped}${settings.decimalSeparator}${fracPart}`;
  const rendered = applyCurrencyTemplate(amount, settings.displayFormat, meta);
  return negative ? `-${rendered}` : rendered;
}

/** Step 1 — the most precise rendering that fits.
 *
 *  Walks decimals down from the currency's own exponent to zero and takes the
 *  first that fits the glyph budget, so precision is given up only as far as
 *  the cell actually forces. Without this a 3-decimal currency is stuck at its
 *  full exponent below {@link WHOLE_UNITS_FLOOR} — "د.ك41.234" is 8 visible
 *  glyphs and simply cannot be compacted, because 41 major units is below the
 *  smallest K/M/B unit.
 *
 *  The floor still applies to the LAST step: dropping a fractional part
 *  entirely is only silent above 100 major units, where it is under 1%. */
function renderWholeUnits(
  amountMinor: number,
  settings: CurrencyFormatSettings,
  meta: CurrencyShape,
  maxLength: number,
): { text: string; dropsFraction: boolean } {
  const abs = Math.abs(amountMinor);
  const minorPerMajor = 10 ** meta.decimals;
  const hasFraction = abs % minorPerMajor !== 0;
  const major = abs / minorPerMajor;
  const negative = amountMinor < 0;

  if (!hasFraction) {
    return {
      text: renderFixed(major, 0, negative, settings, meta),
      dropsFraction: false,
    };
  }

  // Exact first — a short amount keeps every digit and stays byte-identical
  // to `formatMoney`.
  const exact = formatMoney(amountMinor, settings);
  if (visibleLength(exact) <= maxLength) {
    return { text: exact, dropsFraction: false };
  }

  const floor = major >= WHOLE_UNITS_FLOOR ? 0 : 1;
  for (let places = meta.decimals - 1; places >= floor; places--) {
    const text = renderFixed(major, places, negative, settings, meta);
    if (visibleLength(text) <= maxLength) {
      return { text, dropsFraction: true };
    }
  }
  return {
    text: renderFixed(major, floor, negative, settings, meta),
    dropsFraction: true,
  };
}

/** Step 2 — compact to ~3 significant figures with a K / M / B suffix.
 *  Unsigned; the caller re-applies the sign. `null` when the value sits below
 *  the smallest unit and so cannot be compacted at all. */
function renderCompacted(
  major: number,
  settings: CurrencyFormatSettings,
  meta: CurrencyShape,
): { text: string; losesPrecision: boolean } | null {
  const index = UNITS.findIndex((u) => major >= u.divisor);
  if (index === -1) return null;

  const unit = UNITS[index]!;
  const scaled = major / unit.divisor;
  const places = decimalsFor(scaled);
  const rounded = roundTo(scaled, places);

  // 999.7K rounds to 1000K — promote a whole magnitude rather than print a
  // four-digit "compact" value. Already at B (index 0) there is nowhere to go.
  const promoted = rounded >= 1000 ? UNITS[index - 1] : undefined;
  const amount = promoted
    ? `1${promoted.suffix}`
    : `${renderScaled(rounded, places, settings.decimalSeparator)}${unit.suffix}`;

  // Both sides go through `Math.round` because `rounded` is a decimal
  // mantissa: 1.45 is not representable in binary, so `1.45 * 1e6` is not
  // reliably 1450000.
  return {
    text: applyCurrencyTemplate(amount, settings.displayFormat, meta),
    losesPrecision:
      promoted !== undefined ||
      Math.round(rounded * unit.divisor) !== Math.round(major),
  };
}

export interface ApproxMoney {
  /** The rendered price, honouring the agency's symbol, separators and
   *  display template exactly as `formatMoney` would. */
  text: string;
  /** False when `text` no longer states the exact amount — either the
   *  fractional part was rounded away or the value was compacted. Drives the
   *  "prices may be rounded" disclosure, which must appear only when
   *  something on screen was genuinely rounded. A price of 29.00 rendered as
   *  "€29" is still exact. */
  isExact: boolean;
}

/**
 * Space-constrained rendering of an integer minor-unit amount, for surfaces
 * (a calendar day cell) where `formatMoney`'s exact form does not fit.
 *
 * Two steps, in order:
 *   1. Whole major units, when that costs nothing (no fractional part) or
 *      almost nothing (at least {@link WHOLE_UNITS_FLOOR} major units).
 *   2. If it still exceeds `maxLength` glyphs, compact to ~3 significant
 *      figures with a K / M / B suffix.
 *
 * Pure and deterministic, like {@link formatMoney} — no `Intl`, no locale.
 * Rounds to NEAREST, never floor: understating an advertised price is the
 * harmful direction. Any caller that renders an inexact result must expose
 * the exact amount elsewhere (an `aria-label`, a tooltip) and disclose that
 * prices may be rounded.
 */
export function approxMoney(
  amountMinor: number,
  settings: CurrencyFormatSettings,
  maxLength = DEFAULT_APPROX_MAX_LENGTH,
): ApproxMoney {
  if (!Number.isFinite(amountMinor)) return { text: "", isExact: true };

  const meta = currencyMeta(settings.currencyCode);
  const abs = Math.abs(amountMinor);
  const major = abs / 10 ** meta.decimals;

  const whole = renderWholeUnits(amountMinor, settings, meta, maxLength);
  const wholeResult: ApproxMoney = {
    text: whole.text,
    isExact: !whole.dropsFraction,
  };
  if (visibleLength(whole.text) <= maxLength) return wholeResult;

  // A long string can come from a long SYMBOL rather than a large number
  // ("CHF1,234.56"), in which case there is no unit to compact into. Fall
  // back to step 1 and let the caller shrink the type.
  const compact = renderCompacted(major, settings, meta);
  if (!compact) return wholeResult;

  return {
    text: amountMinor < 0 ? `-${compact.text}` : compact.text,
    // Compaction is only exact when the suffix lost nothing — 145,000 → 145K
    // states its amount in full, while 145,123 → 145K does not.
    isExact: !whole.dropsFraction && !compact.losesPrecision,
  };
}

/** {@link approxMoney}'s rendered text. Use when the exactness flag is not
 *  needed; prefer `approxMoney` when driving a rounding disclosure. */
export function formatMoneyApprox(
  amountMinor: number,
  settings: CurrencyFormatSettings,
  maxLength = DEFAULT_APPROX_MAX_LENGTH,
): string {
  return approxMoney(amountMinor, settings, maxLength).text;
}
